import { i18n, type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { z } from 'zod'

import { cmmsCurrency, formatCmmsMoney } from './cmmsMoney'
import catalog from './maximoWorkOrderFields.json'
import type { WorkOrderEntry } from './workOrder'
import { workOrderDate } from './workOrderDates'
import { workOrderPlainText } from './workOrderText'

const text = z
  .union([z.string(), z.number()])
  .nullish()
  .transform((value) =>
    value === null || value === undefined ? '' : String(value)
  )
export const workOrderAdditionalSchema = z.object({
  reportdate: text,
  targstartdate: text,
  targcompdate: text,
  actstart: text,
  actfinish: text,
  owner: text,
  ownergroup: text,
  assignedownergroup: text,
  lead: text,
  supervisor: text,
  persongroup: text,
  crewworkgroup: text,
  vendor: text,
  taskid: text,
  wosequence: text,
  pluscjprevnum: text,
  route: text,
  routestopid: text,
  origrecordid: text,
  origrecordclass: text,
  schedstart: text,
  schedfinish: text,
  respondby: text,
  pmduedate: text,
  pmnextduedate: text,
  estdur: text,
  justifypriority: text,
  wopriority: text,
  wopriority_description: text,
  storeroommtlstatus: text,
  storeroommtlstatus_description: text,
  dirissuemtlstatus: text,
  dirissuemtlstatus_description: text,
  workpackmtlstatus: text,
  workpackmtlstatus_description: text,
  availstatusdate: text,
  estlabhrs: text,
  estatapprlabhrs: text,
  actlabhrs: text,
  estlabcost: text,
  estatapprlabcost: text,
  actlabcost: text,
  estmatcost: text,
  estatapprmatcost: text,
  actmatcost: text,
  estservcost: text,
  estatapprservcost: text,
  actservcost: text,
  esttoolcost: text,
  estatapprtoolcost: text,
  acttoolcost: text,
  risk: text,
  naes_outagetype: text,
  naes_outagetype_description: text,
  naes_outagedur: text,
  naes_outagedur_description: text,
  naes_outageyear: text,
  naes_lotoid: text,
  naes_projectid: text,
  naes_safety: z.boolean().nullish(),
  naes_outage: z.boolean().nullish(),
  naes_loto: z.boolean().nullish(),
  naes_confinedspace: z.boolean().nullish(),
  naes_crane: z.boolean().nullish(),
  naes_environmental: z.boolean().nullish(),
  naes_insulation: z.boolean().nullish(),
  naes_moc: z.boolean().nullish(),
  naes_nde: z.boolean().nullish(),
  naes_nerc: z.boolean().nullish(),
  naes_pwht: z.boolean().nullish(),
  naes_scaffolding: z.boolean().nullish(),
  naes_welding: z.boolean().nullish(),
  siteid: text,
  orgid: text,
  changeby: text,
})
const labels: Record<string, MessageDescriptor> = {
  reportdate: msg`Reported`,
  targstartdate: msg`Target start`,
  targcompdate: msg`Target finish`,
  actstart: msg`Actual start`,
  actfinish: msg`Actual finish`,
  owner: msg`Owner`,
  ownergroup: msg`Owner group`,
  assignedownergroup: msg`Assigned owner group`,
  lead: msg`Lead`,
  supervisor: msg`Supervisor`,
  persongroup: msg`Person group`,
  crewworkgroup: msg`Crew work group`,
  vendor: msg`Vendor`,
  taskid: msg`Task number`,
  wosequence: msg`Task sequence`,
  pluscjprevnum: msg`Job-plan revision`,
  route: msg`Route`,
  routestopid: msg`Route stop`,
  origrecordid: msg`Originating record`,
  origrecordclass: msg`Originating record class`,
  schedstart: msg`Scheduled start`,
  schedfinish: msg`Scheduled finish`,
  respondby: msg`Respond by`,
  pmduedate: msg`PM due`,
  pmnextduedate: msg`Next PM due`,
  estdur: msg`Estimated duration (hours)`,
  justifypriority: msg`Priority justification`,
  wopriority: msg`Work-order priority`,
  wopriority_description: msg`Priority description`,
  storeroommtlstatus: msg`Storeroom material availability`,
  dirissuemtlstatus: msg`Direct-issue material availability`,
  workpackmtlstatus: msg`Work-package material availability`,
  availstatusdate: msg`Material availability updated`,
  estlabhrs: msg`Estimated labor hours`,
  estatapprlabhrs: msg`Estimate at approval: labor hours`,
  actlabhrs: msg`Actual labor hours`,
  estlabcost: msg`Estimated labor cost`,
  estatapprlabcost: msg`Estimate at approval: labor cost`,
  actlabcost: msg`Actual labor cost`,
  estmatcost: msg`Estimated material cost`,
  estatapprmatcost: msg`Estimate at approval: material cost`,
  actmatcost: msg`Actual material cost`,
  estservcost: msg`Estimated service cost`,
  estatapprservcost: msg`Estimate at approval: service cost`,
  actservcost: msg`Actual service cost`,
  esttoolcost: msg`Estimated tool cost`,
  estatapprtoolcost: msg`Estimate at approval: tool cost`,
  acttoolcost: msg`Actual tool cost`,
  siteid: msg`Maximo site`,
  orgid: msg`Maximo organization`,
  changeby: msg`Source changed by`,
}
const definitions = catalog.fields.map((field) => ({
  ...field,
  key: workOrderAdditionalSchema.keyof().parse(field.key),
  labelKey: workOrderAdditionalSchema
    .keyof()
    .safeParse(field.key + '_description'),
}))
const evidenceIds: Record<string, string> = {
  reportdate: 'reported',
  targstartdate: 'targetStart',
  targcompdate: 'targetFinish',
  actstart: 'actualStart',
  actfinish: 'actualFinish',
}

export function workOrderAdditionalFields(entry: WorkOrderEntry) {
  const record = entry.data.record
  return definitions.flatMap((definition) => {
    const raw = record[definition.key]
    if (definition.kind === 'set_flag' && raw !== true) return []
    const value =
      typeof raw === 'boolean' ? String(raw) : workOrderPlainText(raw ?? '')
    if (!value) return []
    const companion = definition.labelKey.success
      ? record[definition.labelKey.data]
      : ''
    const rawValue =
      definition.kind === 'code' && typeof companion === 'string' && companion
        ? value + ' · ' + workOrderPlainText(companion)
        : value
    return [
      {
        id:
          evidenceIds[definition.key] ??
          (definition.section === 'priority' &&
          definition.key !== 'justifypriority'
            ? 'priority'
            : definition.key),
        rawKey: definition.key,
        section: definition.section,
        label:
          definition.section === 'site'
            ? definition.key
            : i18n._(labels[definition.key]),
        value:
          definition.kind === 'date'
            ? workOrderDate(rawValue)
            : definition.key.endsWith('cost')
              ? formatCmmsMoney(rawValue, cmmsCurrency(record))
              : rawValue,
        evidence: definition.label + ' (' + definition.key + '): ' + rawValue,
      },
    ]
  })
}

export const workOrderFieldReferences = catalog.references
